import {
  useCallback,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { useParams } from 'react-router-dom';

import {
  ModelWarehouseInventoryState,
  useCompleteWarehouseInventoryMutation,
  useGetInventoryChangesQuery,
  useGetWarehouseInventoryByIdQuery,
  useGetWarehouseInventoryReportQuery,
  useGetWarehouseInvetoryProductsQuery,
  useLazyExportInventoryReportToExcelQuery,
  useLevelUpInventoryReportMutation,
} from '@app/api/WarehouseInventories';
import { XLSX } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { InventoryCardContext } from '@app/providers/InventoryCardProvider/InventoryCardContext';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

const { Provider } = InventoryCardContext;

export const InventoryCardProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { inventoryId: inventoryIdQuery } = useParams<{
    inventoryId: string;
  }>();

  const [isFinishButtonDisabled, setIsFinishButtonDisabled] = useState(false);
  const [completeDisabledTooltip, setCompleteDisabledTooltip] = useState<
    string | null
  >(null);

  const inventoryId = useMemo(() => {
    return inventoryIdQuery && !isNaN(Number(inventoryIdQuery))
      ? Number(inventoryIdQuery)
      : 0;
  }, [inventoryIdQuery]);
  const { showNotification } = useNotification();

  const {
    data: inventory,
    isLoading: isInventoryLoading,
    isFetching: isInventoryFetching,
    isError: isInventoryError,
    error: inventoryErrorData,
    refetch: refetchInventory,
  } = useGetWarehouseInventoryByIdQuery(
    {
      ActiveBranchId,
      inventoryId,
    },
    {
      skip: !inventoryId,
      refetchOnMountOrArgChange: true,
    }
  );

  const status = useMemo(() => {
    if (!inventory) return null;

    return inventory.State;
  }, [inventory]);

  const params = useMemo(
    () => ({
      ActiveBranchId,
      inventoryId,
    }),
    [ActiveBranchId, inventoryId]
  );

  const inProgressSettings = useMemo(
    () => ({
      skip: !inventoryId || status !== ModelWarehouseInventoryState.Open,
      refetchOnMountOrArgChange: true,
    }),
    [inventoryId, status]
  );

  const [
    completeInventory,
    {
      isLoading: isCompleteInventoryLoading,
      isError: isCompleteInventoryError,
      error: completeInventoryErrorData,
    },
  ] = useCompleteWarehouseInventoryMutation();

  const [exportInventoryReport, { isFetching: isExportLoading }] =
    useLazyExportInventoryReportToExcelQuery();

  const [
    levelUpInventoryReport,
    {
      isLoading: isLevelUpInventoryReportLoading,
      isError: isLevelUpInventoryReportError,
      error: levelUpInventoryReportErrorData,
    },
  ] = useLevelUpInventoryReportMutation();

  const {
    data: inventoryReport,
    isLoading: isInventoryReportLoading,
    isFetching: isInventoryReportFetching,
    isError: isInventoryReportError,
    error: inventoryReportErrorData,
    refetch: refetchInventoryReport,
  } = useGetWarehouseInventoryReportQuery(params, {
    skip: !inventoryId,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: inventoryChanges,
    isLoading: isInventoryChangesLoading,
    isFetching: isInventoryChangesFetching,
    isError: isInventoryChangesError,
    error: inventoryChangesErrorData,
    refetch: refetchInventoryChanges,
  } = useGetInventoryChangesQuery(params, inProgressSettings);

  const {
    data: inventoryProducts,
    isLoading: isInventoryProductsLoading,
    isFetching: isInventoryProductsFetching,
    isError: isInventoryProductsError,
    error: inventoryProductsErrorData,
    refetch: refetchInventoryProducts,
  } = useGetWarehouseInvetoryProductsQuery(params, inProgressSettings);

  useErrorHandler([
    { flag: isCompleteInventoryError, error: completeInventoryErrorData },
    {
      flag: isLevelUpInventoryReportError,
      error: levelUpInventoryReportErrorData,
    },
    { flag: isInventoryError, error: inventoryErrorData },
    { flag: isInventoryReportError, error: inventoryReportErrorData },
    { flag: isInventoryChangesError, error: inventoryChangesErrorData },
    {
      flag: isInventoryProductsError,
      error: inventoryProductsErrorData,
    },
  ]);

  const hasInventoryReportDifferences = useMemo(
    () =>
      inventoryReport?.Products?.some(
        ({ CountDifference }) => CountDifference !== 0
      ) ?? false,
    [inventoryReport]
  );

  const onComplete = useCallback(async () => {
    if (!inventoryId) return;

    try {
      const { Warehouse } = await completeInventory({
        ActiveBranchId,
        inventoryId,
      }).unwrap();

      refetchInventory();
      refetchInventoryReport();
      refetchInventoryChanges();
      refetchInventoryProducts();

      showNotification(
        `Инвентаризация ${Warehouse} успешно завершена`,
        'success'
      );
    } catch (error) {
      showNotification(error);
    }
  }, [
    inventoryId,
    showNotification,
    ActiveBranchId,
    completeInventory,
    refetchInventory,
    refetchInventoryReport,
    refetchInventoryChanges,
    refetchInventoryProducts,
  ]);

  const onExportToExcel = useCallback(async () => {
    if (!inventoryId || !inventory) return;
    if (inventory.State !== ModelWarehouseInventoryState.Done) return;

    try {
      const base64 = await exportInventoryReport({
        ActiveBranchId,
        inventoryId,
      }).unwrap();

      const link = document.createElement('a');
      link.href = `data:${XLSX};base64,${base64}`;
      link.download = `Инвентаризация №${inventory.Id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showNotification(error);
    }
  }, [
    inventoryId,
    inventory,
    ActiveBranchId,
    exportInventoryReport,
    showNotification,
  ]);

  const onLevelUpInventoryReport = useCallback(async () => {
    if (!inventoryId || !ActiveBranchId) return;
    if (status !== ModelWarehouseInventoryState.Done) return;
    if (!hasInventoryReportDifferences) return;

    try {
      await levelUpInventoryReport({
        ActiveBranchId,
        inventoryId,
      }).unwrap();

      await refetchInventoryReport();
      showNotification('Остатки успешно выровнены', 'success');
    } catch (error) {
      showNotification(error);
    }
  }, [
    inventoryId,
    ActiveBranchId,
    status,
    hasInventoryReportDifferences,
    levelUpInventoryReport,
    refetchInventoryReport,
    showNotification,
  ]);

  const isPageLoading = useMemo(() => {
    switch (status) {
      case ModelWarehouseInventoryState.Open:
        return isInventoryChangesLoading || isInventoryChangesFetching;
      default:
        return (
          isInventoryReportLoading ||
          isInventoryReportFetching ||
          isInventoryChangesLoading ||
          isInventoryChangesFetching ||
          isInventoryProductsLoading ||
          isInventoryProductsFetching ||
          isInventoryLoading ||
          isInventoryFetching
        );
    }
  }, [
    status,
    isInventoryReportLoading,
    isInventoryReportFetching,
    isInventoryChangesLoading,
    isInventoryChangesFetching,
    isInventoryProductsLoading,
    isInventoryProductsFetching,
    isInventoryLoading,
    isInventoryFetching,
  ]);

  const refetchInventoryProductsWrapper = useCallback(async () => {
    await refetchInventoryProducts();
  }, [refetchInventoryProducts]);

  return (
    <Provider
      value={{
        isCompleteInventoryLoading,
        isExportLoading,
        isLevelUpInventoryReportLoading,
        hasInventoryReportDifferences,
        onComplete,
        onExportToExcel,
        onLevelUpInventoryReport,
        inventory,
        inventoryReport,
        inventoryChanges,
        inventoryProducts,
        isPageLoading,
        refetchInventoryProducts: refetchInventoryProductsWrapper,
        isFinishButtonDisabled,
        completeDisabledTooltip,
        setIsFinishButtonDisabled,
        setCompleteDisabledTooltip,
      }}
    >
      {children}
    </Provider>
  );
};
